import { defineStore } from 'pinia'
import { ref, computed, watch, toRaw } from 'vue'
import { useCustomerStore } from './customer'
import { number as yupNumber, object as yupObject } from 'yup'
import i18n, { localeTag } from '../i18n/index.js'

// Store-side translation helper (see cart.js). The schema is a computed, so
// these resolve at validation time — i18n is initialized by then.
const t = (key) => i18n.global.t(key)

export const useScheduleStore = defineStore('schedule', () => {
  const customerStore = useCustomerStore()

  // ============================================
  // PLAIN STATE (no vee-validate — survives navigation)
  // ============================================
  const selectedDate = ref(null)   // timestamp (ms) or null
  const selectedTime = ref(null)   // minutes (0-1439) or null

  // Validation state managed manually
  const errors = ref({ selectedDate: null, selectedTime: null })
  const touched = ref({ selectedDate: false, selectedTime: false })

  // Get merged settings (service-specific overrides global)
  const settings = computed(() => {
    const global = window['_EZCL']?.data?.ordering?.schedule || {}
    const serviceType = customerStore.orderType
    const serviceSettings = window['_EZCL']?.data?.services?.[serviceType]?.schedule || {}

    return {
      enabled: serviceSettings.enabled ?? global.enabled ?? false,
      days: serviceSettings.days ?? global.days ?? 3,
      daysBuffer: serviceSettings.daysBuffer ?? global.daysBuffer ?? 0,
      prepTime: serviceSettings.prepTime ?? global.prepTime ?? 15,
      timeStep: serviceSettings.timeStep ?? global.timeStep ?? 15,
      use24hFormat: serviceSettings.use24hFormat ?? global.use24hFormat ?? false,
      stableSchedule: serviceSettings.stableSchedule ?? global.stableSchedule ?? null,
    }
  })

  // Add ref for tracking
  const lastRefreshedAt = ref(Date.now())

  // Refresh available slots (call this on component mount / visibility change)
  function refreshSlots() {
    lastRefreshedAt.value = Date.now()
  }

  // Check if selection is stale
  const isSelectionStale = computed(() => {
    if (!selectedDate.value || selectedTime.value === null) return false

    const now = new Date()
    const selectedDateObj = new Date(selectedDate.value)

    // If selected date is in the past
    if (selectedDateObj < new Date(now.toDateString())) return true

    // If today and selected time has passed
    if (selectedDateObj.toDateString() === now.toDateString()) {
      const nowMinutes = now.getHours() * 60 + now.getMinutes()
      if (selectedTime.value < nowMinutes + settings.value.prepTime) return true
    }

    return false
  })

  // Parse stableSchedule { from: 'HH:MM', to: 'HH:MM' }
  const businessHours = computed(() => {
    const schedule = settings.value.stableSchedule
    if (!schedule?.from || !schedule?.to) return null
    return {
      start: parseTimeString(schedule.from),
      end: parseTimeString(schedule.to),
    }
  })

  const openingHours = computed(() => {
    return window['_EZCL']?.data?.address?.oh || []
  })

  const weekdayKey = (value) => {
    return String(value || '').trim().toLowerCase().slice(0, 3)
  }

  const weekdayKeyFromDate = (date) => {
    return weekdayKey(new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date(date)))
  }

  const getOpeningHoursDay = (date) => {
    const targetDay = weekdayKeyFromDate(date)
    return openingHours.value.find(day => weekdayKey(day?.name) === targetDay) || null
  }

  const isDateOpenForScheduling = (date) => {
    const day = getOpeningHoursDay(date)
    return !day || Number(day.status) >= 1
  }

  // Parse "HH:MM" to minutes
  function parseTimeString(str) {
    const [h, m] = str.split(':').map(Number)
    return h * 60 + m
  }

  // Get time slots for a specific date
  function getTimeSlotsForDate(date) {
    if (!date) return []
    if (!isDateOpenForScheduling(date)) return []

    const { prepTime, timeStep } = settings.value
    const hours = businessHours.value
    const slots = []
    const now = new Date()
    const selected = new Date(date)
    const isToday = selected.toDateString() === now.toDateString()

    let startMinutes = hours ? hours.start : 0
    let endMinutes = hours ? hours.end : 24 * 60

    if (isToday) {
      const nowMinutes = now.getHours() * 60 + now.getMinutes() + prepTime
      const roundedNow = Math.ceil(nowMinutes / timeStep) * timeStep
      startMinutes = Math.max(startMinutes, roundedNow)
    }

    // No slots if start >= end
    if (startMinutes >= endMinutes) return []

    for (let mins = startMinutes; mins < endMinutes; mins += timeStep) {
      slots.push(mins)
    }

    return slots
  }

  // Available dates (only those with available time slots)
  const availableDates = computed(() => {
    const { days, daysBuffer } = settings.value
    const dates = []
    const now = new Date()

    for (let i = daysBuffer; i < daysBuffer + days; i++) {
      const date = new Date(now)
      date.setDate(date.getDate() + i)
      date.setHours(0, 0, 0, 0)

      // Only include dates that have available time slots
      if (getTimeSlotsForDate(date).length > 0) {
        dates.push(date)
      }
    }
    return dates
  })

  // Available timestamps for quick lookup
  const availableDateTimestamps = computed(() => {
    return availableDates.value.map(d => d.getTime())
  })

  // Min/max date boundaries
  const minDate = computed(() => {
    return availableDates.value.length ? availableDates.value[0] : null
  })

  const maxDate = computed(() => {
    return availableDates.value.length
      ? availableDates.value[availableDates.value.length - 1]
      : null
  })

  // ============================================
  // YUP VALIDATION SCHEMA (kept as-is, used manually)
  // ============================================
  const validationSchema = computed(() => {
    const availableTimestamps = availableDateTimestamps.value
    const currentSettings = settings.value

    return yupObject({
      selectedDate: yupNumber()
        .nullable()
        .required(t('schedule.selectDate'))
        .test(
          'is-available-date',
          t('schedule.dateUnavailable'),
          (value) => {
            if (!value) return false
            return availableTimestamps.includes(value)
          }
        )
        .test(
          'is-not-past',
          t('schedule.datePast'),
          (value) => {
            if (!value) return false
            const now = new Date()
            now.setHours(0, 0, 0, 0)
            return value >= now.getTime()
          }
        )
        .test(
          'is-not-too-future',
          t('schedule.maxDaysAhead', currentSettings.days, { n: currentSettings.days }),
          (value) => {
            if (!value) return false
            const maxAllowed = new Date()
            maxAllowed.setDate(maxAllowed.getDate() + currentSettings.daysBuffer + currentSettings.days)
            maxAllowed.setHours(0, 0, 0, 0)
            return value < maxAllowed.getTime()
          }
        ),

      selectedTime: yupNumber()
        .nullable()
        .required(t('schedule.selectTime'))
        .test(
          'is-valid-time-range',
          t('schedule.timeRange'),
          (value) => {
            if (value === null || value === undefined) return false
            return value >= 0 && value < 1440
          }
        )
        .test(
          'is-available-slot',
          t('schedule.slotUnavailable'),
          function (value) {
            if (value === null || value === undefined) return false
            const { selectedDate } = this.parent
            if (!selectedDate) return false
            const slots = getTimeSlotsForDate(selectedDate)
            return slots.includes(value)
          }
        )
        .test(
          'is-not-past-time',
          t('schedule.timePast'),
          function (value) {
            if (value === null || value === undefined) return false
            const { selectedDate } = this.parent
            if (!selectedDate) return false

            const now = new Date()
            const selected = new Date(selectedDate)

            // Not today - skip this check
            if (selected.toDateString() !== now.toDateString()) return true

            const nowMinutes = now.getHours() * 60 + now.getMinutes()
            return value >= nowMinutes + currentSettings.prepTime
          }
        )
        .test(
          'is-within-business-hours',
          t('schedule.outsideHours'),
          (value) => {
            if (value === null || value === undefined) return false
            const hours = businessHours.value
            if (!hours) return true // No restrictions
            return value >= hours.start && value < hours.end
          }
        ),
    })
  })

  // ============================================
  // MANUAL VALIDATION (replaces vee-validate)
  // ============================================
  async function _runValidation() {
    try {
      await validationSchema.value.validate(
        { selectedDate: selectedDate.value, selectedTime: selectedTime.value },
        { abortEarly: false }
      )
      errors.value = { selectedDate: null, selectedTime: null }
      return { valid: true }
    } catch (yupError) {
      const newErrors = { selectedDate: null, selectedTime: null }
      if (yupError.inner) {
        for (const err of yupError.inner) {
          if (err.path && !newErrors[err.path]) {
            newErrors[err.path] = err.message
          }
        }
      }
      errors.value = newErrors
      return { valid: false }
    }
  }

  async function _validateField(fieldName) {
    try {
      await validationSchema.value.validateAt(fieldName, {
        selectedDate: selectedDate.value,
        selectedTime: selectedTime.value,
      })
      errors.value = { ...errors.value, [fieldName]: null }
      return { valid: true }
    } catch (yupError) {
      errors.value = { ...errors.value, [fieldName]: yupError.message }
      return { valid: false, error: yupError.message }
    }
  }

  // ============================================
  // COMPUTED
  // ============================================
  const availableTimeSlots = computed(() => {
    return getTimeSlotsForDate(selectedDate.value)
  })

  // Check if current selection is ASAP
  const isAsap = computed(() => {
    if (selectedDate.value === null || selectedTime.value === null) return false

    const now = new Date()
    now.setHours(0, 0, 0, 0)

    // Must be today
    if (selectedDate.value !== now.getTime()) return false

    // Must be first available slot
    const slots = getTimeSlotsForDate(selectedDate.value)
    return slots.length > 0 && selectedTime.value === slots[0]
  })

  // Format time slot for display
  const formatTimeSlot = (minutes, dateTimestamp = null) => {
    const checkDate = dateTimestamp ?? selectedDate.value

    if (checkDate) {
      const now = new Date()
      now.setHours(0, 0, 0, 0)

      // If today and first slot — show ASAP
      if (checkDate === now.getTime()) {
        const slots = getTimeSlotsForDate(checkDate)
        if (slots.length > 0 && minutes === slots[0]) {
          return t('schedule.asap')
        }
      }
    }

    const h = Math.floor(minutes / 60)
    const m = minutes % 60

    if (settings.value.use24hFormat) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    }

    const period = h >= 12 ? 'PM' : 'AM'
    const h12 = h % 12 || 12
    return `${h12}:${String(m).padStart(2, '0')} ${period}`
  }

  const formattedSelectedTime = computed(() => {
    return selectedTime.value !== null ? formatTimeSlot(selectedTime.value) : null
  })

  // Human-readable date shown to the customer (ThankYou) and carried into the
  // merchant's POS order note. Localized for display; the machine-readable
  // scheduling wire-format is isoSelectedDate ('YYYY-MM-DD'), which is
  // locale-INDEPENDENT and drives all validation — so localizing this is safe.
  const formattedSelectedDate = computed(() => {
    if (!selectedDate.value) return null
    return new Date(selectedDate.value).toLocaleDateString(localeTag(), {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    })
  })

  // Check if scheduling is available (enabled + has dates)
  const isAvailable = computed(() => {
    return settings.value.enabled && availableDates.value.length > 0
  })

  const isValid = computed(() => !errors.value.selectedDate && !errors.value.selectedTime && selectedDate.value !== null && selectedTime.value !== null)
  const isDirty = computed(() => selectedDate.value !== null || selectedTime.value !== null)
  const isTouched = computed(() => touched.value.selectedDate || touched.value.selectedTime)

  // Combined datetime for submission
  const selectedDateTime = computed(() => {
    if (!selectedDate.value || selectedTime.value === null) return null
    const date = new Date(selectedDate.value)
    const hours = Math.floor(selectedTime.value / 60)
    const minutes = selectedTime.value % 60
    date.setHours(hours, minutes, 0, 0)
    return date
  })

  // Wire-format date/time for backend conversion to merchant-TZ timestamp.
  // 'YYYY-MM-DD' / 'HH:MM' (24h). Null when nothing selected.
  const isoSelectedDate = computed(() => {
    if (!selectedDate.value) return null
    const d = new Date(selectedDate.value)
    const pad = n => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  })
  const isoSelectedTime = computed(() => {
    if (selectedTime.value === null) return null
    const pad = n => String(n).padStart(2, '0')
    return `${pad(Math.floor(selectedTime.value / 60))}:${pad(selectedTime.value % 60)}`
  })

  // ============================================
  // WATCHERS
  // ============================================

  // When date changes: reset invalid time + re-validate
  watch(selectedDate, async (newDate) => {
    if (newDate) {
      touched.value.selectedTime = true

      // Reset time if no longer valid for new date
      if (selectedTime.value !== null) {
        const slots = getTimeSlotsForDate(newDate)
        if (!slots.includes(selectedTime.value)) {
          selectedTime.value = null
        }
      }

      // Re-validate time field (cross-field dependency)
      await _validateField('selectedTime')
    }
  })

  // Auto-fix invalid date when available dates change
  watch(availableDates, (dates, oldDates) => {
    if (!oldDates || dates.length === 0) return
    if (selectedDate.value && !dates.some(d => d.getTime() === selectedDate.value)) {
      selectedDate.value = dates.length ? dates[0].getTime() : null
    }
  })

  // Re-validate when settings change
  watch(settings, async () => {
    if (isTouched.value) {
      await _runValidation()
    }
  }, { deep: true })

  // ============================================
  // METHODS
  // ============================================
  async function setDate(date) {
    selectedDate.value = date ? new Date(date).getTime() : null
    touched.value.selectedDate = true

    if (date) {
      touched.value.selectedTime = true
      await _validateField('selectedTime')
    }

    await _validateField('selectedDate')
  }

  async function setTime(minutes) {
    selectedTime.value = minutes
    touched.value.selectedTime = true
    await _validateField('selectedTime')
  }

  async function resetDateTime() {
    selectedDate.value = null
    selectedTime.value = null
    touched.value = { selectedDate: false, selectedTime: false }
    const result = await _runValidation()
    return result
  }

  async function validateForm() {
    // Touch all fields to show errors
    touched.value = { selectedDate: true, selectedTime: true }
    const result = await _runValidation()
    return result
  }

  async function validateDate() {
    touched.value.selectedDate = true
    const result = await _validateField('selectedDate')
    return {
      valid: result.valid,
      error: result.valid ? null : errors.value.selectedDate
    }
  }

  async function validateTime() {
    touched.value.selectedTime = true
    const result = await _validateField('selectedTime')
    return {
      valid: result.valid,
      error: result.valid ? null : errors.value.selectedTime
    }
  }

  // Validate both and return detailed result
  async function validateAll() {
    touched.value = { selectedDate: true, selectedTime: true }

    const { valid } = await _runValidation()

    return {
      valid,
      errors: {
        date: errors.value.selectedDate || null,
        time: errors.value.selectedTime || null,
      },
      values: valid ? {
        date: selectedDate.value,
        time: selectedTime.value,
        dateTime: selectedDateTime.value,
      } : null,
    }
  }

  function reset() {
    selectedDate.value = null
    selectedTime.value = null
    touched.value = { selectedDate: false, selectedTime: false }
    errors.value = { selectedDate: null, selectedTime: null }
  }

  // Get raw yup schema for external use
  function getValidationSchema() {
    return toRaw(validationSchema.value)
  }

  return {
    // State
    selectedDate,
    selectedTime,
    isAsap,

    // Errors & meta
    errors,
    touched,
    isValid,
    isDirty,
    isTouched,

    // Computed
    settings,
    availableDates,
    availableTimeSlots,
    formattedSelectedTime,
    formattedSelectedDate,
    selectedDateTime,
    isoSelectedDate,
    isoSelectedTime,
    isAvailable,
    minDate,
    maxDate,
    businessHours,

    // Methods
    setDate,
    setTime,
    resetDateTime,
    lastRefreshedAt,
    isSelectionStale,
    refreshSlots,
    formatTimeSlot,
    validate: validateForm,
    validateDate,
    validateTime,
    validateAll,
    reset,
    getValidationSchema,
    getTimeSlotsForDate,
  }
}, {
  persist: {
    pick: ['selectedDate', 'selectedTime'],
    storage: 'sessionStorage',
    key: `_ezcl/${window.location.hostname}/${window['_EZCL']?.data.id}/${window['_EZCL']?.data.pageId}/schedule`,
    afterRestore: (ctx) => {
      // Revalidate after hydration
      ctx.store.validate()
    },
  },
})
